/**
 * One standards schema, for every course.
 *
 * THE PROBLEM
 *
 * Algebra I Level 1 carries its alignment in four fields — `standards` (which
 * silently means CCSS), `practices` (which silently means the CCSS Standards
 * for Mathematical Practice), `teks` and `teksPractices`. That shape says
 * "there are exactly two frameworks and one of them is the default", and it is
 * wrong the moment a Geometry unit needs a third citation, or a Trigonometry
 * unit needs to cite CCSS at the *conceptual category* level instead of the
 * domain level.
 *
 * THE SHAPE
 *
 * A node aligns through one array. Every entry names its own framework, so
 * adding a framework is adding rows, not editing readers:
 *
 *   "alignment": [
 *     { "framework": "CCSS-M", "code": "HSG.CO.C.9",
 *       "text": "Prove theorems about lines and angles.", "depth": "core" },
 *     { "framework": "TEKS",   "code": "G.6(A)",
 *       "citation": "19 TAC §111.41(c)(6)(A)",
 *       "text": "verify theorems about angles…", "depth": "core" }
 *   ],
 *   "practices": {
 *     "CCSS-M": ["MP.3", "MP.6"],
 *     "TEKS":   ["G.1(G)"]
 *   },
 *   "alignmentNote": "…what the mastery gate actually tests, per framework."
 *
 * `depth` is one of `core`, `supporting`, `introduced`. It is the honesty
 * field: `core` means the mastery gate for this node actually proves the
 * expectation. Nothing may claim `core` that the gate does not test.
 *
 * NOTHING BREAKS. `alignmentOf()` reads the legacy four-field shape and the
 * unified one and returns the same list either way, so Algebra I Level 1 is not
 * edited, not re-verified and not re-translated, and every reader — the
 * dossier, the teacher report, the content gate — sees one shape.
 */

/** The frameworks this game knows how to cite, and how to name them. */
export const FRAMEWORKS = {
  // The legal names of two published standards frameworks. They are proper
  // nouns and are not translated in any of the three locales — a Spanish report
  // still cites "Common Core" and "TEKS", because that is what the document is
  // called. i18n-allow: the published name of a standards framework
  'CCSS-M': { id: 'CCSS-M', label: 'Common Core (CCSS-M)', short: 'CCSS' },
  // i18n-allow: the published name of a standards framework
  TEKS: { id: 'TEKS', label: 'Texas Essential Knowledge and Skills', short: 'TEKS' },
};

export const DEPTHS = ['core', 'supporting', 'introduced'];

/**
 * Every standard a node cites, in one shape.
 *
 * A MISSING DEPTH IS NOT `core`, AND THIS FUNCTION USED TO SAY IT WAS.
 *
 * `depth` defaulted to `'core'` on both shapes. That is the strongest claim the
 * schema can make, handed out for free to any row that forgot to state one —
 * and Algebra I Level 1 forgot on all thirty-five of its Common Core rows.
 * Nine of those thirty-five are `supporting` or `introduced` in
 * `content/standards/ccss-algebra1-l1.json`, which is the document a district
 * reads, so the graph and the map said different things about the same nine
 * standards. The screen happened to read the map and was right; `COURSES.md`
 * tells a new course to read the graph, and a new course would have silently
 * upgraded nine standards to core on its first day.
 *
 * So nothing is invented here. An undeclared depth comes back `null`, every
 * reader has to decide what to do about it in the open, and
 * `tools/validate-courses.mjs` — which already refuses any depth outside
 * `DEPTHS` — now fails the build rather than being satisfied by a default.
 * The thirty-five Level 1 rows carry their depth explicitly, copied from the
 * map they were always supposed to agree with.
 *
 * @param {object} node a knowledge-graph node
 * @returns {Array<{framework:string, code:string, text:string, depth:string|null,
 *                  citation?:string, caveat?:string, i18n?:object}>}
 */
export function alignmentOf(node) {
  if (!node) return [];
  if (Array.isArray(node.alignment)) {
    return node.alignment.map((a) => ({
      framework: a.framework || 'CCSS-M',
      code: a.code,
      text: a.text,
      depth: a.depth || null,
      citation: a.citation,
      caveat: a.caveat,
      i18n: a.i18n,
    }));
  }
  const out = [];
  for (const s of node.standards || []) {
    out.push({
      framework: 'CCSS-M', code: s.code, text: s.text,
      depth: s.depth || null, caveat: s.caveat, i18n: s.i18n,
    });
  }
  for (const s of node.teks || []) {
    out.push({
      framework: 'TEKS', code: s.code, text: s.text,
      depth: s.depth || null, citation: s.citation, caveat: s.caveat, i18n: s.i18n,
    });
  }
  return out;
}

/**
 * The practice / process standards a node claims, keyed by framework.
 * @returns {Object<string, string[]>}
 */
export function practicesOf(node) {
  if (!node) return {};
  if (node.practices && !Array.isArray(node.practices)) {
    const out = {};
    for (const [fw, list] of Object.entries(node.practices)) out[fw] = [...list];
    return out;
  }
  const out = {};
  if (Array.isArray(node.practices) && node.practices.length) out['CCSS-M'] = [...node.practices];
  if (Array.isArray(node.teksPractices) && node.teksPractices.length) out.TEKS = [...node.teksPractices];
  return out;
}

/** The prose that says what the gate really proves, per framework. */
export function alignmentNote(node, framework) {
  if (!node) return '';
  if (node.alignmentNote && typeof node.alignmentNote === 'object') {
    return node.alignmentNote[framework] || node.alignmentNote.all || '';
  }
  if (typeof node.alignmentNote === 'string') return node.alignmentNote;
  if (framework === 'TEKS') return node.teksNote || '';
  return node.standardsNote || '';
}

/** Just the citations for one framework. */
export function codesFor(node, framework) {
  return alignmentOf(node).filter((a) => a.framework === framework).map((a) => a.code);
}

/** Which frameworks a whole graph cites. */
export function frameworksOf(graph) {
  const set = new Set();
  for (const n of graph.nodes || []) for (const a of alignmentOf(n)) set.add(a.framework);
  return [...set];
}

/**
 * A flat coverage table: one row per (framework, code) with the nodes that
 * claim it and the deepest claim made. This is what a teacher report and the
 * content gate both want, and neither should have to build it.
 */
export function coverage(graph) {
  const rows = new Map();
  for (const n of graph.nodes || []) {
    for (const a of alignmentOf(n)) {
      const key = `${a.framework} ${a.code}`;
      const row = rows.get(key) || { framework: a.framework, code: a.code, text: a.text, nodes: [], depth: 'introduced' };
      row.nodes.push(n.id);
      // An undeclared depth cannot strengthen a row. `indexOf` returns -1 for
      // null, which would sort ahead of `core` and quietly promote the whole
      // expectation — the exact bug the default used to cause one row at a time.
      if (DEPTHS.includes(a.depth) && DEPTHS.indexOf(a.depth) < DEPTHS.indexOf(row.depth)) row.depth = a.depth;
      rows.set(key, row);
    }
  }
  return [...rows.values()];
}
